/* eslint-disable */
// NotificationRow — one row in the notification drawer, plus the "Archived · Undo"
// line that replaces it. Split out of NotificationDrawer to keep both files under
// the 300-line cap (Conv #6 / #163).
//
// ⚠️ Content renders as TEXT. No dangerouslySetInnerHTML — this list shows
// admin-supplied titles and bodies, so one unescaped field would be a stored XSS.
// ⚠️ dir="auto" on title and body: a Hebrew body must right-align, and the row's
// frame must not move when it does.
// ⚠️ No notification content in any data-test value (spec §5.8).

import React from 'react';
import NotificationAttachments from './NotificationAttachments';
import CollectionMovedChips, { COLLECTION_MOVED } from './CollectionMovedChips';
import styles from './notificationDrawer.module.css';
import { canOpenNotification, openNotification } from '../../utils/entityRoutes';

function relativeTime(iso) {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

// ⚠️ The payload-image read path was removed here (P6 §2.4) — see the longer
// note in NotificationCard.js. Nothing ever wrote those keys, so no shipped row
// changes; but a URL in a shared row escapes the access filter, and attachments
// (kind + id, resolved per reader) replace it.

const ArchiveIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="4" width="18" height="4" rx="1" />
    <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
    <path d="M10 12h4" />
  </svg>
);

// The row after archiving: it stays in place for ~5s offering an undo, then
// collapses. Nothing is ever deleted — archived_at is a stamp, and P4 ships no
// archived view, which is exactly why this undo is not optional (spec §5.5).
export function ArchivedUndoRow({ onUndo }) {
  return (
    <div className={styles.undoRow} data-test="notification-archived-undo">
      <span>Archived</span>
      <button type="button" className={styles.undoBtn} onClick={onUndo} data-test="notification-undo">
        Undo
      </button>
    </div>
  );
}

export function NotificationRow({ row, senderName, isActive, onRead, onArchive, onNavigate }) {
  const n = row.notifications;
  const unread = !row.read_at;
  const who = senderName || 'System';

  // Clicking an already-read row does nothing — not an error, and never a
  // toggle back to unread.
  const handleClick = () => { if (unread) onRead(row.id); };

  return (
    <div
      className={`${styles.row}${unread ? '' : ' ' + styles.readRow}${isActive ? ' ' + styles.active : ''}`}
      role="option"
      aria-selected={!!isActive}
      tabIndex={-1}
      onClick={handleClick}
      data-test="notification-row"
      data-unread={unread ? 'true' : 'false'}
    >
      <span className={styles.rail}>{unread && <span className={styles.dot} />}</span>


      <div className={styles.rowText}>
        <div className={styles.rowTitle} dir="auto">{n.title}</div>
        {n.body && <div className={styles.rowBody} dir="auto">{n.body}</div>}
        <div className={styles.rowMeta}>{who} · {relativeTime(n.created_at)}</div>
        {/* §2.3 — nothing attached renders nothing, so a pre-P6 row is untouched */}
        {/* Navigating closes the drawer, otherwise the record opens
            underneath it. The attachment stops its own click (AttachmentCard),
            so the row's mark-as-read is NOT also fired. */}
        <NotificationAttachments payload={n.payload} entityType={n.entity_type} entityId={n.entity_id} onNavigate={onNavigate} />
        {n.type_key === COLLECTION_MOVED ? <CollectionMovedChips n={n} /> : null}
      </div>

      {canOpenNotification(n) && (
        <button
          type="button"
          className={styles.rowOpen}
          onClick={(e) => { e.stopPropagation(); if (openNotification(n)) onRead(row.id); }}
          data-test="notification-row-open"
        >Open</button>
      )}

      <button
        type="button"
        className={styles.archiveBtn}
        onClick={(e) => { e.stopPropagation(); onArchive(row.id); }}
        aria-label="Archive notification"
        title="Archive"
        data-test="notification-archive"
      >
        <ArchiveIcon />
      </button>
    </div>
  );
}

export default NotificationRow;
